import { formatYYYYMMDD } from '@webapp/core/utils/dates/formatYYYYMMDD/formatYYYYMMDD'
import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import {
  type HttpResponse,
  type IHttpClient,
} from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type IPersistentStorage } from '@webapp/infrastructure/interfaces/services/IPersistentStorage'
import { type IOrderItemRepository } from '@webapp/modules/purchasing/business-logic/interfaces/IOrderItemRepository'
import { type OrderItemMutationData } from '@webapp/modules/purchasing/business-logic/mutations/OrderItemMutation'
import {
  type AccountSuggestion,
  type AccountSuggestionConstructor,
} from '@webapp/modules/purchasing/entities/AccountSuggestion'
import { type ApprovedOrderItemConstructor } from '@webapp/modules/purchasing/entities/ApprovedOrderItem'
import { OrderItemBlanketReceiveMethods } from '@webapp/modules/purchasing/entities/enums/OrderItemBlanketReceiveMethodEnum'
import { type OrderItemRecurrenceDurationEnum } from '@webapp/modules/purchasing/entities/enums/OrderItemRecurrenceDurationEnum'
import { type OrderItemRecurrenceFrequencyEnum } from '@webapp/modules/purchasing/entities/enums/OrderItemRecurrenceFrequencyEnum'
import { type OrderItemConstructorData } from '@webapp/modules/purchasing/entities/OrderItem'
import {
  type OrderItemVendor,
  type OrderItemVendorConstructor,
} from '@webapp/modules/purchasing/entities/OrderItemVendor'
import {
  type AccountConstructor,
  type BudgetPeriodConstructor,
  type IAccountRepository,
} from '@webapp/platform/finance'
import { type ApprovedOrderItemApiModel } from '../../api-models/ApprovedOrderItemApiModel'
import { type BudgetPeriodApiModel } from '../../api-models/BudgetPeriodApiModel'
import { type PreferredVendorApiModel } from '../../api-models/PreferredVendorApiModel'
import { type SuggestionsApiModel } from '../../api-models/SuggestionsApiModel'
import { mapApprovedOrderItemApiModel } from '../../mappers/mapApprovedOrderItemApiModel'
import { mapBudgetPeriodApiModel } from '../../mappers/mapBudgetPeriodApiModel'
import { mapOrderItemApiModel } from '../../mappers/mapOrderItemApiModel'
import { mapPreferredVendorApiModel } from '../../mappers/mapPreferredVendorApiModel'
import { type ApprovedOrderItemListParams } from '../../types/ApprovedOrderItemListParams'
import { type GetDiffbotResponse } from '../../types/GetDiffbotResponse'
import { type GetPurchasingListItemsResponse } from '../../types/GetPurchasingListItemsResponse'
import { type GetRecurrencePeriodResponse } from '../../types/GetRecurrencePeriodResponse'
import { type GetRequestablePreferredVendorsResponse } from '../../types/GetRequestablePreferredVendorsResponse'
import { type GetUnitTypeResponse } from '../../types/GetUnitTypeResponse'
import { type GetVendorDetailsResponse } from '../../types/GetVendorDetailsResponse'
import { type PutEditOrderItemPayload } from '../../types/PutEditOrderItemPayload'
import { type PutEditOrderItemResponse } from '../../types/PutEditOrderItemResponse'

interface Dependency {
  httpClient: IHttpClient
  accountRepository: IAccountRepository
  persistentStorage: IPersistentStorage
}

interface GetApprovedOrderItemsResponse {
  data: ApprovedOrderItemApiModel[]
  metadata: {
    pagination: {
      count: number
      current_page: number
      page_size: number
      num_pages: number
    }
  }
}

export class OrderItemRepository implements IOrderItemRepository {
  private readonly MAX_SUGGESTIONS = 3

  constructor(private readonly deps: Dependency) {}

  private getOrderItemSuggestionStorageKey(userId: number): string {
    return `procurify.order-item-suggestions_${userId}`
  }

  async getAllActiveUnitTypes(searchTerm?: string): Promise<string[]> {
    try {
      const res = await this.deps.httpClient.get<GetUnitTypeResponse>(
        `/api/unit/`,
        {
          params: {
            active: true,
            page_size: 'all',
            search: searchTerm,
          },
        }
      )

      return res.data.data.map((unitType) => unitType.name)
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async listApprovedItems(
    params?: ApprovedOrderItemListParams
  ): Promise<RepoPaginatedResponse<ApprovedOrderItemConstructor>> {
    const {
      primaryCategoryId,
      secondaryCategoryId,
      requesterId,
      submittedDateStart,
      submittedDateEnd,
      approvedDateStart,
      approvedDateEnd,
      ...filterParams
    } = params ?? {}

    const formatDateParam = (value?: string) => {
      if (!value) return undefined
      // Already a YYYY-MM-DD wire value — reparsing would shift the day
      // across timezones
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
      return formatYYYYMMDD(new Date(value))
    }

    try {
      const res = await this.deps.httpClient.get<GetApprovedOrderItemsResponse>(
        '/api/v3/order-items/approved/',
        {
          params: {
            ...formatRepoFiltersToApiFilters(filterParams),
            location: primaryCategoryId,
            department: secondaryCategoryId,
            requester: requesterId,
            order_created_date_0: formatDateParam(submittedDateStart),
            order_created_date_1: formatDateParam(submittedDateEnd),
            approved_datetime_0: formatDateParam(approvedDateStart),
            approved_datetime_1: formatDateParam(approvedDateEnd),
          },
        }
      )

      const metadata = res.data.metadata

      return {
        data: res.data.data.map((item) => mapApprovedOrderItemApiModel(item)),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async getApprovedItemCount(): Promise<number> {
    try {
      const res = await this.deps.httpClient.get<GetApprovedOrderItemsResponse>(
        '/api/v3/order-items/approved/',
        {
          params: {
            page_size: 1,
          },
        }
      )

      return res.data.metadata?.pagination?.count || 0
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async getBudgetPeriod(
    orderItemId: number,
    accountId: number
  ): Promise<BudgetPeriodConstructor> {
    try {
      const res = await this.deps.httpClient.get<BudgetPeriodApiModel>(
        `/api/v3/order-items/${orderItemId}/accounts/${accountId}/budget-period/`
      )

      return mapBudgetPeriodApiModel(res.data)
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async correctBudgets(
    corrections: { orderItemId: number; accountId: number }[]
  ): Promise<void> {
    try {
      await this.deps.httpClient.put(
        '/api/v3/order-items/budgets/',
        corrections.map((correction) => ({
          orderitem: correction.orderItemId,
          account: correction.accountId,
        }))
      )
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  getRequestableAccountsBySecondaryCategory(
    secondaryCategoryId: number,
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<AccountConstructor>> {
    return this.deps.accountRepository.getRequestableAccounts(
      secondaryCategoryId,
      params
    )
  }

  async getRequestablePreferredVendors(
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<OrderItemVendorConstructor>> {
    try {
      const res =
        await this.deps.httpClient.get<GetRequestablePreferredVendorsResponse>(
          `/api/v3/vendors/preferred/`,
          {
            params: {
              ...formatRepoFiltersToApiFilters(params),
            },
          }
        )

      const vendors = res.data.data
      const metadata = res.data.metadata

      return {
        data: vendors.map((vendor) => mapPreferredVendorApiModel(vendor)),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async extractProductDataFromUrl(productUrl: string): Promise<{
    name?: string
    sku?: string
    price?: number
    productUrl: string
  }> {
    let data: GetDiffbotResponse['data']

    try {
      const res = await this.deps.httpClient.get<GetDiffbotResponse>(
        '/api/v3/diffbot/',
        {
          params: {
            url: encodeURI(productUrl),
          },
        }
      )

      data = res.data?.data

      if (!data || 'errorCode' in data) {
        throw new Error()
      }
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }

    const extractedProduct = data?.objects?.[0]
    let extractedPrice = 0

    if (
      extractedProduct.offerPriceDetails &&
      extractedProduct.offerPriceDetails.amount
    ) {
      extractedPrice = extractedProduct.offerPriceDetails.amount
    } else if (extractedProduct.offerPrice) {
      extractedPrice = Number(
        extractedProduct.offerPrice.replace(/[^0-9\.]+/g, '')
      )
    }

    return {
      name: extractedProduct?.title,
      sku: extractedProduct?.sku,
      price: extractedPrice,
      productUrl,
    }
  }

  async getAccountCodeSuggestions(
    userId: number,
    secondaryCategoryId?: number
  ): Promise<AccountSuggestionConstructor[]> {
    const storageKey = this.getOrderItemSuggestionStorageKey(userId)

    // While suggestions should be stored in a way that matches the SuggestionsApiModel,
    // there's a chance that the data has been corrupted. As such, we need to be cautious
    // here and ensure that we're not trying to access properties that don't exist.
    const fullHistory: SuggestionsApiModel =
      (await this.deps.persistentStorage.get(storageKey)) || {
        account: [],
        vendor: [],
      }

    const accountPromise: Promise<AccountConstructor | null>[] = []

    for (const account of fullHistory?.account || []) {
      const promise = this.deps.accountRepository
        .getAccountById(account.id)
        .catch(() => null)
      accountPromise.push(promise)
    }

    let accountSuggestions = (await Promise.all(accountPromise)).reduce(
      (acc: AccountSuggestionConstructor[], account) => {
        if (account && account.active && account.secondaryCategory?.id) {
          acc.push({
            accountCode: {
              code: account.accountCode.code,
              description: account.accountCode.description,
            },
            id: account.id,
            department: {
              id: account.secondaryCategory.id,
            },
          })
        }
        return acc
      },
      [] as AccountSuggestionConstructor[]
    )

    if (secondaryCategoryId !== undefined) {
      accountSuggestions = accountSuggestions.filter((account) => {
        return account.department.id === secondaryCategoryId
      })
    }

    return accountSuggestions.slice(0, this.MAX_SUGGESTIONS)
  }

  async saveAccountCodeSuggestions(
    userId: number,
    suggestions: AccountSuggestion[]
  ): Promise<void> {
    const storageKey = this.getOrderItemSuggestionStorageKey(userId)

    const fullHistory: SuggestionsApiModel =
      (await this.deps.persistentStorage.get(storageKey)) || {
        account: [],
        vendor: [],
      }

    const formattedSuggestions: SuggestionsApiModel['account'] =
      suggestions.map((suggestion) => ({
        account_code: {
          code: suggestion.accountCode.code,
          description: suggestion.accountCode.description,
        },
        department: {
          id: suggestion.department.id,
        },
        id: suggestion.id,
      }))

    fullHistory.account = [...formattedSuggestions]

    this.deps.persistentStorage.set(storageKey, fullHistory)
  }

  async getPreferredVendorSuggestions(
    userId: number
  ): Promise<OrderItemVendorConstructor[]> {
    const storageKey = this.getOrderItemSuggestionStorageKey(userId)

    const fullHistory: SuggestionsApiModel =
      (await this.deps.persistentStorage.get(storageKey)) || {
        account: [],
        vendor: [],
      }

    const slicedVendorSuggestions = fullHistory.vendor.slice(
      0,
      this.MAX_SUGGESTIONS
    )

    const vendorPromise: Promise<PreferredVendorApiModel | null>[] = []

    for (const vendor of slicedVendorSuggestions) {
      const promise = this.deps.httpClient
        .get<GetVendorDetailsResponse>(`/api/v3/vendors/${vendor.id}/`)
        .then((res) => {
          return res.data.data
        })
        .catch(() => null)

      vendorPromise.push(promise)
    }

    const resolvedVendors = (await Promise.all(vendorPromise))
      .map((vendorDetail) => {
        if (vendorDetail) {
          return {
            id: vendorDetail.id,
            isAutoEmailPoEnabled: vendorDetail.is_auto_email_po_enabled,
            name: vendorDetail.name,
          } as OrderItemVendorConstructor
        }
        return null
      })
      // Remove any vendor that cannot be resolved. This can happen if the vendor is deleted.
      .filter(Boolean)

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    return (resolvedVendors as OrderItemVendorConstructor[]) || []
  }

  async savePreferredVendorSuggestions(
    userId: number,
    vendorSuggestions: OrderItemVendor[]
  ): Promise<void> {
    const storageKey = this.getOrderItemSuggestionStorageKey(userId)

    const fullHistory: SuggestionsApiModel =
      (await this.deps.persistentStorage.get(storageKey)) || {
        account: [],
        vendor: [],
      }

    fullHistory.vendor = [...vendorSuggestions]

    this.deps.persistentStorage.set(storageKey, fullHistory)
  }

  async getRecurrencePeriods({
    duration,
    durationQuantity,
    frequency,
    startDate,
  }: {
    duration: OrderItemRecurrenceDurationEnum
    durationQuantity: number
    frequency: OrderItemRecurrenceFrequencyEnum
    startDate: string
  }): Promise<number> {
    try {
      const res = await this.deps.httpClient.get<GetRecurrencePeriodResponse>(
        '/api/v3/order-items/recurrence/periods-count/',
        {
          params: {
            duration,
            duration_quantity: durationQuantity,
            frequency,
            start_date: startDate,
          },
        }
      )

      const data = res.data.data

      return data.periods_count
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async denyOrderItem(orderId: number, orderItemId: number): Promise<void> {
    try {
      await this.deps.httpClient.post(
        `/approvals/pending/${orderId}/`,
        new URLSearchParams({
          action: 'reject_item',
          pk: orderItemId.toString(),
        }).toString()
      )
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async undoDenyOrderItem(orderId: number, orderItemId: number): Promise<void> {
    try {
      await this.deps.httpClient.post(
        `/approvals/pending/${orderId}/`,
        new URLSearchParams({
          action: 'undo_reject_item',
          pk: orderItemId.toString(),
        }).toString()
      )
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async editItem(
    orderItem: OrderItemMutationData,
    confirmedBudgetOverage?: Nullable<boolean>
  ): Promise<OrderItemConstructorData> {
    if (!orderItem.id) {
      throw new RepoError({
        code: null,
        status: null,
        message: 'Order item id is required',
        response: null,
      })
    }

    if (!orderItem.preferredVendor) {
      throw new RepoError({
        code: null,
        status: null,
        message: 'Preferred vendor is required',
        response: null,
      })
    }

    if (!orderItem.account) {
      throw new RepoError({
        code: null,
        status: null,
        message: 'Account is required',
        response: null,
      })
    }

    if (!orderItem.currency) {
      throw new RepoError({
        code: null,
        status: null,
        message: 'Currency is required',
        response: null,
      })
    }

    try {
      const res = await this.deps.httpClient.put<
        PutEditOrderItemResponse,
        PutEditOrderItemPayload
      >(
        `/api/v3/order-items/${orderItem.id}/`,
        {
          type: orderItem.type,
          unit: orderItem.unitType,
          vendor: orderItem.vendor,
          pref_vendor: orderItem.preferredVendor.id,
          sku: orderItem.sku,
          name: orderItem.name,
          price: orderItem.price?.toString() ?? null,
          quantity: orderItem.quantity?.toString() || '',
          line_comment: orderItem.comments,
          currency: orderItem.currency.id,
          account: orderItem.account.id,
          item_attachments: orderItem.attachments.map(
            (attachment) => attachment.uuid
          ),
          custom_fields: orderItem.customFields
            .map((customFieldValue) => {
              if (!customFieldValue.id && !customFieldValue.value) return null

              return {
                field: customFieldValue.field.id,
                value: customFieldValue.value,
              }
            })
            .filter((customFieldValue) => customFieldValue !== null),
          recurrence: orderItem.recurrence
            ? {
                duration: orderItem.recurrence.duration,
                duration_quantity: orderItem.recurrence.durationQuantity,
                frequency: orderItem.recurrence.frequency,
                recurring_price:
                  orderItem.recurrence.recurringPrice?.toString() ?? null,
                start_date: formatYYYYMMDD(
                  new Date(orderItem.recurrence.startDate)
                ),
              }
            : null,
          blanket: orderItem.blanket
            ? {
                amount:
                  orderItem.blanketReceiveMethod ===
                  OrderItemBlanketReceiveMethods.CURRENCY_AMOUNT
                    ? (orderItem.blanket.amount?.toString() ?? null)
                    : null,
                price:
                  orderItem.blanketReceiveMethod ===
                  OrderItemBlanketReceiveMethods.CURRENCY_AMOUNT
                    ? '1'
                    : (orderItem.blanket.price?.toString() ?? null),
                quantity:
                  orderItem.blanketReceiveMethod ===
                  OrderItemBlanketReceiveMethods.CURRENCY_AMOUNT
                    ? null
                    : orderItem.blanket.quantity.toString(),
              }
            : null,
          receive_method: orderItem.blanketReceiveMethod ?? undefined,
          catalog_item: orderItem.catalogItemRef,
        },
        {
          params: {
            confirmed_budget_overage: confirmedBudgetOverage,
          },
        }
      )

      return mapOrderItemApiModel(res.data.data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getPurchasingListItems(): Promise<OrderItemConstructorData[]> {
    try {
      const res =
        await this.deps.httpClient.get<GetPurchasingListItemsResponse>(
          '/api/v3/order-items/purchasing-list/'
        )
      return res.data.data.map((item) => mapOrderItemApiModel(item))
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async validateOrderItemEditedCost({
    orderItemId,
    quantity,
    price,
    taxAmount,
    shippingAmount,
  }: {
    orderItemId: number
    quantity: number
    price: number
    taxAmount?: Nullable<number>
    shippingAmount?: Nullable<number>
  }): Promise<HttpResponse<void>> {
    try {
      return await this.deps.httpClient.post(
        `/api/v3/order-items/cost-limit/`,
        {
          order_item_id: orderItemId,
          quantity,
          price,
          tax_amount: taxAmount,
          shipping_amount: shippingAmount,
        }
      )
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }
}
